import { RotateCcw } from "lucide-react";
import { formatMoney, toDecimal } from "../../lib/money";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { signedMoney } from "./DiffView";
import type { RevisionSummary } from "../../api/types";

// 版本清單,最新的排最前面(順序由後端決定,這裡不重排)。
//
// 每一列點下去就是「拿這一版跟目前這一版比」。比較的終點固定是最新的那一版 ——
// 業主看版本紀錄要問的是「跟現在差在哪」,不是「第 2 版跟第 3 版差在哪」。
//
// 金額的增減值一律用後端算好的 total_delta:前端自己減就得先湊齊全部版本再排序,
// 而且瀏覽器的數字是浮點數,遲早會冒出 0.30000000000000004 那種數字給客戶看。

/**
 * 一整列的欄位(業主 2026-09-17:「版本顯示寫的超亂 規範一下 要整齊」)。
 *
 * 版本 / 時間 / 建立者 / 金額 / 差額 / 標記,**六格,每一列都畫滿**。
 *
 * 原本是 `flex-wrap` 加一個 `ml-auto`:金額被推到「剩下的空間」的最左邊,
 * 而剩下的空間由它後面那幾顆會變的標記決定 —— 每一列的標記數量不一樣,
 * 金額就落在不同的水平位置,差額更亂。欄位要由版面決定,不由內容決定。
 *
 * 前三欄自己收縮(`minmax(0,…)` + `truncate`),金額與差額固定寬度 ——
 * 數字那兩欄一旦會伸縮,對齊就沒有意義了。
 */
const ROW_GRID =
  "grid grid-cols-[minmax(0,4.5rem)_minmax(0,1fr)_minmax(0,3rem)_6rem_5.5rem_minmax(0,9rem)]";

export interface RevisionListProps {
  revisions: RevisionSummary[];
  loading: boolean;
  error: string | null;
  /** 目前選為比較起點的版本。 */
  fromVersion: number | null;
  /** 報價單目前的版本號,用來標出「目前版本」。 */
  currentVersion: number;
  onSelect: (version: number) => void;
}

export function RevisionList({
  revisions,
  loading,
  error,
  fromVersion,
  currentVersion,
  onSelect,
}: RevisionListProps) {
  if (error !== null) {
    return (
      <p role="alert" className="m-0 px-3 py-3 text-sm text-[var(--danger)]">
        {error}
      </p>
    );
  }
  if (loading) return <p className="m-0 px-3 py-3 text-sm text-[var(--text-3)]">載入中…</p>;
  if (revisions.length === 0) {
    return <p className="m-0 px-3 py-3 text-sm text-[var(--text-3)]">這份報價單還沒有版本紀錄。</p>;
  }

  return (
    // 固定欄寬的代價是窄螢幕上塞不下,而**把欄位壓扁比橫向捲更糟** ——
    // 壓扁就回到了「每一列的數字落在不同位置」那個病。所以給它自己的橫捲,
    // 與明細表格、報表同一個處理方式:整頁不橫捲,只有這一塊捲。
    <div className="w-full overflow-x-auto">
    <ul
      aria-label="版本清單"
      className="m-0 flex min-w-[32rem] list-none flex-col rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-0"
    >
      {revisions.map((revision, index) => {
        // 最新的那一版是比較的終點,不能同時當起點,所以它自己不可選。
        const isLatest = index === 0;
        const isOldest = index === revisions.length - 1;
        const selected = revision.version === fromVersion;
        const delta = toDecimal(revision.total_delta).isZero() ? "" : signedMoney(revision.total_delta);

        return (
          <li key={revision.version} className="border-t border-[var(--border)] first:border-t-0">
            <button
              type="button"
              disabled={isLatest}
              aria-pressed={isLatest ? undefined : selected}
              onClick={() => onSelect(revision.version)}
              data-testid="revision-row"
              className={
                // **固定欄位的 grid,不是會換行的 flex。** 見檔頭:欄位由版面決定,
                // 不由內容決定 —— 標記多一顆就把金額推走的那一版正是這樣壞的。
                `${ROW_GRID} w-full items-center gap-x-3 px-3 py-2 text-left ` +
                "disabled:cursor-default " +
                (selected ? "bg-[var(--surface-2)] " : "hover:bg-[var(--surface-2)] ")
              }
            >
              <span
                data-testid="revision-cell-version"
                className="tabular truncate text-sm font-medium text-[var(--text)]"
              >
                版本 {revision.version}
              </span>
              <span
                data-testid="revision-cell-when"
                className="tabular truncate text-xs text-[var(--text-3)]"
              >
                {formatTaipeiDateTime(revision.created_at)}
              </span>
              <span
                data-testid="revision-cell-who"
                className="truncate text-xs text-[var(--text-2)]"
              >
                {revision.created_by_name}
              </span>
              {/* 數字一律右對齊:位數不同的金額要對得起來,靠的是小數點那一端。 */}
              <span
                data-testid="revision-cell-total"
                className="tabular text-right text-sm text-[var(--text)]"
              >
                {formatMoney(revision.total)}
              </span>
              <span
                data-testid="revision-cell-delta"
                className="tabular text-right text-xs text-[var(--text-2)]"
              >
                {delta}
              </span>
              {/* 標記全部收在最後一格裡 —— 有幾顆都不會影響前面那幾欄的位置。
                  標記一律帶文字,不靠顏色分辨。 */}
              <span
                data-testid="revision-cell-marks"
                className="flex flex-wrap items-center justify-end gap-1"
              >
                {revision.version === currentVersion ? (
                  <span className="shrink-0 rounded-[var(--radius)] border border-[var(--accent)] px-1.5 py-0.5 text-xs text-[var(--accent-hover)]">
                    目前版本
                  </span>
                ) : null}
                {selected ? (
                  <span className="shrink-0 rounded-[var(--radius)] border border-[var(--border-strong)] px-1.5 py-0.5 text-xs text-[var(--text-2)]">
                    比較起點
                  </span>
                ) : null}
                {revision.is_restore_of !== null ? (
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--text-2)]">
                    <RotateCcw aria-hidden="true" size={12} />
                    復原自版本 {revision.is_restore_of}
                  </span>
                ) : null}
                {isOldest ? <span className="shrink-0 text-xs text-[var(--text-3)]">建立</span> : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
    </div>
  );
}
